import { deleteProjectFile, listProjectFiles, moveProjectFile } from '../api/uploadApi';
import { UploadResultDto } from '../core/types';
import { formatBytes } from '../core/format';
import { escapeHtml, makeButton, requireElement } from './dom';

interface TreeFolderNode {
  type: 'folder';
  name: string;
  path: string;
  folders: Map<string, TreeFolderNode>;
  files: Map<string, TreeFileNode>;
}

interface TreeFileNode {
  type: 'file';
  name: string;
  file: UploadResultDto;
}

function buildTree(files: UploadResultDto[]): TreeFolderNode {
  const root: TreeFolderNode = { type: 'folder', name: '', path: '', folders: new Map(), files: new Map() };

  for (const file of files) {
    const segments = file.relativePath.split('/').filter(Boolean);
    const fileName = segments.pop() ?? file.originalFileName;
    let cursor = root;
    let pathSoFar = '';

    for (const segment of segments) {
      pathSoFar = pathSoFar ? `${pathSoFar}/${segment}` : segment;
      let next = cursor.folders.get(segment);
      if (!next) {
        next = { type: 'folder', name: segment, path: pathSoFar, folders: new Map(), files: new Map() };
        cursor.folders.set(segment, next);
      }
      cursor = next;
    }

    cursor.files.set(fileName, { type: 'file', name: fileName, file });
  }

  return root;
}

/**
 * Shows a real folder tree of previously completed uploads — separate from
 * FileListView, which only shows the current browser session's in-flight
 * queue. Lets a user find, move, or delete a file uploaded in an earlier
 * session (e.g. one dropped into the wrong folder), which a flat list of
 * slash-separated names doesn't support well.
 */
export class UploadedFilesView {
  private readonly container: HTMLElement;
  private readonly emptyMessage: HTMLElement;
  private projectName: string | null = null;
  private readonly collapsedPaths = new Set<string>();

  constructor() {
    this.container = requireElement('uploaded-files-tree');
    this.emptyMessage = requireElement('uploaded-files-empty');
  }

  async setProject(projectName: string | null): Promise<void> {
    this.projectName = projectName;
    await this.refresh();
  }

  async refresh(): Promise<void> {
    if (!this.projectName) {
      this.render([]);
      return;
    }
    try {
      const files = await listProjectFiles(this.projectName);
      this.render(files);
    } catch {
      this.render([]);
    }
  }

  private render(files: UploadResultDto[]): void {
    this.container.querySelectorAll('.tree-folder, .tree-file-row').forEach((el) => el.remove());

    if (files.length === 0) {
      this.emptyMessage.classList.remove('hidden');
      return;
    }
    this.emptyMessage.classList.add('hidden');

    const root = buildTree(files);
    for (const el of this.renderFolderContents(root)) {
      this.container.appendChild(el);
    }
  }

  private renderFolderContents(folder: TreeFolderNode): HTMLElement[] {
    const elements: HTMLElement[] = [];

    const sortedFolders = [...folder.folders.values()].sort((a, b) => a.name.localeCompare(b.name));
    for (const child of sortedFolders) {
      elements.push(this.renderFolder(child));
    }

    const sortedFiles = [...folder.files.values()].sort((a, b) => a.name.localeCompare(b.name));
    for (const child of sortedFiles) {
      elements.push(this.renderFileRow(child));
    }

    return elements;
  }

  private renderFolder(folder: TreeFolderNode): HTMLElement {
    const isCollapsed = this.collapsedPaths.has(folder.path);

    const wrapper = document.createElement('div');
    wrapper.className = `tree-folder${isCollapsed ? ' collapsed' : ''}`;

    const header = document.createElement('div');
    header.className = 'tree-folder-header';
    header.innerHTML = `<span class="tree-toggle">▾</span><span>📁 ${escapeHtml(folder.name)}</span>`;
    header.addEventListener('click', () => {
      if (this.collapsedPaths.has(folder.path)) this.collapsedPaths.delete(folder.path);
      else this.collapsedPaths.add(folder.path);
      wrapper.classList.toggle('collapsed');
    });
    wrapper.appendChild(header);

    const childrenContainer = document.createElement('div');
    childrenContainer.className = 'tree-children';
    for (const el of this.renderFolderContents(folder)) {
      childrenContainer.appendChild(el);
    }
    wrapper.appendChild(childrenContainer);

    return wrapper;
  }

  private renderFileRow(node: TreeFileNode): HTMLElement {
    const row = document.createElement('div');
    row.className = 'tree-file-row';
    row.innerHTML = `
      <span class="tree-file-name">📄 ${escapeHtml(node.name)}</span>
      <span class="tree-file-meta">${formatBytes(node.file.sizeBytes)} · ${escapeHtml(new Date(node.file.uploadedAt).toLocaleString())}</span>
    `;

    const actions = document.createElement('div');
    actions.className = 'tree-file-actions';
    actions.appendChild(makeButton('이동', () => void this.handleMove(node.file)));
    actions.appendChild(makeButton('삭제', () => void this.handleDelete(node.file)));
    row.appendChild(actions);

    return row;
  }

  private async handleMove(file: UploadResultDto): Promise<void> {
    if (!this.projectName) return;
    const newRelativePath = window.prompt('새 경로 (폴더/파일명)을 입력하세요', file.relativePath);
    if (!newRelativePath || newRelativePath === file.relativePath) return;

    try {
      await moveProjectFile(this.projectName, file.absolutePath, newRelativePath);
      await this.refresh();
    } catch (error) {
      window.alert(`이동 실패: ${(error as Error).message}`);
    }
  }

  private async handleDelete(file: UploadResultDto): Promise<void> {
    if (!this.projectName) return;
    const confirmed = window.confirm(`"${file.relativePath}" 파일을 삭제할까요? 이 작업은 되돌릴 수 없습니다.`);
    if (!confirmed) return;

    try {
      await deleteProjectFile(this.projectName, file.absolutePath);
      await this.refresh();
    } catch (error) {
      window.alert(`삭제 실패: ${(error as Error).message}`);
    }
  }
}
